import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "config/firebase";
import { useEffect, useState } from "react";

const workspaceImageKey = (workspaceId?: string | number, path?: string | null) =>
  ["workspace", "image", workspaceId ?? null, path ?? null] as const;

export const useWorkspaceImage = (workspaceId: string | number, imagePath?: string | null) => {
  const queryClient = useQueryClient();
  const sessionKey = imagePath
    ? `workspaceImage-${workspaceId}-${imagePath}`
    : `workspaceImage-${workspaceId}`;
  const [hadError, setHadError] = useState(false);
  const optimisticKey = ["workspace", "image", workspaceId ?? null, "optimistic-upload"] as const;
  const optimisticQuery = useQuery(
    optimisticKey,
    async () => (queryClient.getQueryData(optimisticKey) as string | undefined) || "",
    {
      initialData: () => (queryClient.getQueryData(optimisticKey) as string | undefined) || "",
      staleTime: Infinity,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      enabled: true,
    }
  );
  const optimisticUrl = (optimisticQuery.data as string) || undefined;

  const query = useQuery(
    workspaceImageKey(workspaceId, imagePath || undefined),
    async () => {
      if (!storage) {
        throw new Error("Firebase Storage is not initialized");
      }
      const cached = sessionStorage.getItem(sessionKey);
      if (cached) return cached;
      const src = await getDownloadURL(ref(storage, imagePath!));
      sessionStorage.setItem(sessionKey, src);
      return src;
    },
    {
      enabled: !!imagePath && !!storage,
      staleTime: Infinity,
      cacheTime: 60 * 60 * 1000,
    }
  );

  useEffect(() => {
    setHadError(false);
  }, [imagePath, query.data]);

  const onImageError = () => setHadError(true);
  const resolvedSrc = optimisticUrl ?? ((query.data as string) || "");
  // Not `isLoading`: without Firebase storage the request never runs, and a disabled query
  // stays loading in React Query v4, which left a skeleton instead of the initial.
  const isLoading = optimisticUrl ? false : query.isInitialLoading;
  const shouldShowImage = Boolean(resolvedSrc) && !isLoading && !hadError;

  return {
    image: { isLoading, src: resolvedSrc },
    hasImagePath: !!imagePath,
    shouldShowImage,
    onImageError,
  };
};
